import { isKnownCountry, partnerNumber } from '@loot/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  api,
  type PartnerGeneralStatus,
  type PartnerLinkStatus,
  type PartnerSummary,
  type RestrictionArea,
} from '../../api.ts';
import { FormModal, FormSection } from '../../components/FormModal.tsx';
import { Badge, Button, Field, Select, Textarea } from '../../components/ui.tsx';

type Tone = 'neutral' | 'ok' | 'off' | 'info' | 'accent';

const GENERAL_TONE: Record<PartnerGeneralStatus, Tone> = {
  active: 'ok',
  pending: 'accent',
  blocked: 'off',
  inactive: 'neutral',
};

const LINK_TONE: Record<PartnerLinkStatus, Tone> = {
  approved: 'ok',
  pending: 'accent',
  rejected: 'off',
  inactive: 'neutral',
};

export function GeneralStatusBadge({
  status,
  hasEntityRestrictions,
}: {
  status: PartnerGeneralStatus;
  hasEntityRestrictions?: boolean;
}) {
  const { t } = useTranslation();
  const label =
    status === 'active' && hasEntityRestrictions
      ? t('partners.statuses.activeRestricted')
      : t(`partners.statuses.${status}`);
  return <Badge tone={GENERAL_TONE[status]}>{label}</Badge>;
}

export function LinkStatusBadge({ status }: { status: PartnerLinkStatus }) {
  const { t } = useTranslation();
  return <Badge tone={LINK_TONE[status]}>{t(`partners.linkStatuses.${status}`)}</Badge>;
}

export function AreaBadges({ areas }: { areas: RestrictionArea[] }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex flex-wrap gap-1">
      {areas.map((a) => (
        <Badge key={a} tone="off">
          {t(`partners.areas.${a}`)}
        </Badge>
      ))}
    </span>
  );
}

export function AptBadge({ apt }: { apt: boolean }) {
  const { t } = useTranslation();
  return (
    <Badge tone={apt ? 'ok' : 'neutral'}>
      {apt ? t('partners.apt.yes') : t('partners.apt.no')}
    </Badge>
  );
}

/** Documento com a pontuação de costume (CPF, CNPJ, inclusive o alfanumérico). */
export function formatTaxId(type: PartnerSummary['taxIdType'], value: string | null): string {
  if (!value) return '—';
  if (type === 'cpf' && value.length === 11) {
    return `${value.slice(0, 3)}.${value.slice(3, 6)}.${value.slice(6, 9)}-${value.slice(9)}`;
  }
  if (type === 'cnpj' && value.length === 14) {
    return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8, 12)}-${value.slice(12)}`;
  }
  return value;
}

/**
 * Fornecedor num campo de escolha: nome, documento e código, para achar pelo CNPJ, pelo documento
 * estrangeiro (EIN e outros) ou pelo código, com ou sem a pontuação.
 */
export function partnerChoice(p: {
  id: string;
  name: string;
  number: number;
  taxIdType: PartnerSummary['taxIdType'];
  taxId: string | null;
}) {
  const doc = p.taxId ? formatTaxId(p.taxIdType, p.taxId) : null;
  return {
    id: p.id,
    label: [p.name, doc, partnerNumber(p.number)].filter(Boolean).join(' · '),
    search: [p.taxId, p.taxId?.replace(/\W/g, ''), String(p.number)].filter(Boolean).join(' '),
  };
}

let countryCodes: string[] | null = null;

/** Códigos ISO 3166 que o navegador conhece como países (calculado uma vez só). */
function allCountryCodes(): string[] {
  if (!countryCodes) {
    countryCodes = [];
    for (let a = 65; a <= 90; a++) {
      for (let b = 65; b <= 90; b++) {
        const code = String.fromCharCode(a, b);
        if (isKnownCountry(code)) countryCodes.push(code);
      }
    }
  }
  return countryCodes;
}

/** Países com o nome no idioma da pessoa, em ordem alfabética. */
export function useCountries() {
  const language = useTranslation().i18n.language;
  return useMemo(() => {
    const names = new Intl.DisplayNames([language], { type: 'region' });
    const list = allCountryCodes().map((code) => ({ code, name: names.of(code) ?? code }));
    list.sort((x, y) => x.name.localeCompare(y.name, language));
    return list;
  }, [language]);
}

export function useCountryName() {
  const language = useTranslation().i18n.language;
  return useMemo(() => {
    const names = new Intl.DisplayNames([language], { type: 'region' });
    return (code: string) => names.of(code) ?? code;
  }, [language]);
}

export function CountrySelect({
  id,
  value,
  onChange,
  exclude = [],
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (code: string) => void;
  /** Países que não entram na lista (ex.: o Brasil, no fornecedor internacional). */
  exclude?: string[];
  /** Texto da opção vazia: obriga a escolher um país. */
  placeholder?: string;
}) {
  const countries = useCountries();
  return (
    <Select
      id={id}
      value={value}
      required={placeholder !== undefined}
      onChange={(e) => onChange(e.target.value)}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {countries
        .filter((c) => !exclude.includes(c.code))
        .map((c) => (
          <option key={c.code} value={c.code}>
            {c.name}
          </option>
        ))}
    </Select>
  );
}

/** Envia uma ação da ficha e recarrega os dados. */
export function usePartnerAction(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      method = 'POST',
      path,
      body,
    }: {
      method?: 'POST' | 'PUT';
      path: string;
      body?: unknown;
    }) =>
      method === 'PUT'
        ? api.put(`/partners/${id}${path}`, body)
        : api.post(`/partners/${id}${path}`, body ?? {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['partners'] });
    },
  });
}

/** Botão que pede um comentário (motivo ou justificativa) antes de confirmar. */
export function CommentAction({
  label,
  fieldLabel,
  variant = 'secondary',
  pending,
  error,
  onConfirm,
}: {
  label: string;
  fieldLabel: string;
  variant?: 'primary' | 'secondary' | 'danger';
  pending: boolean;
  /** Erro da ação; aparece na janela quando vem do envio feito nela. */
  error?: unknown;
  onConfirm: (comment: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [comment, setComment] = useState('');
  const wasPending = useRef(false);
  // Fecha quando o envio feito nesta janela termina sem erro (a ação pode continuar na tela,
  // como Inativar e Reativar).
  useEffect(() => {
    if (open && sent && wasPending.current && !pending && !error) {
      setOpen(false);
      setSent(false);
      setComment('');
    }
    wasPending.current = pending;
  }, [open, sent, pending, error]);
  const close = () => {
    setOpen(false);
    setSent(false);
    setComment('');
  };
  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <FormModal
        open={open}
        size="md"
        title={label}
        dirty={comment.trim() !== ''}
        saving={pending}
        error={sent ? error : null}
        saveLabel={label}
        saveTone={variant === 'danger' ? 'danger' : 'primary'}
        onSave={() => {
          setSent(true);
          onConfirm(comment.trim());
        }}
        onClose={close}
      >
        <FormSection columns={1}>
          <Field label={fieldLabel} required>
            <Textarea required value={comment} onChange={(e) => setComment(e.target.value)} />
          </Field>
        </FormSection>
      </FormModal>
    </>
  );
}
